import React from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiCopy, FiPlus } from "react-icons/fi";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import { pickString, readSessionJson } from "./api/payoutMutationHelpers";

const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const displayText = (value?: string) => value?.trim() || "-";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 last:border-b-0 md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#505E8E]">{label}</span>
      <div className="text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function PayoutBulkUploadConfirm() {
  const draft = readSessionJson<{
    fileName?: string;
    rows?: Record<string, unknown>[];
  }>("payoutBulkTransferDraft", {});
  const result = readSessionJson<Record<string, unknown>>(
    "payoutBulkTransferResult",
    {},
  );
  const resultItems = Array.isArray(result.items)
    ? (result.items as Record<string, unknown>[])
    : [];
  const draftRows = Array.isArray(draft.rows) ? draft.rows : [];
  const createdCount = resultItems.length || draftRows.length;
  const totalAmount = (resultItems.length ? resultItems : draftRows).reduce(
    (sum, row) => sum + (Number(row.amount_paise) || Number(row.amountPaise) || 0) / 100,
    0,
  );
  const batchId = pickString(result, ["batch_id", "batchId"], "-");
  const batchNo = pickString(result, ["batch_no", "batchNo"], batchId);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Confirm Bulk Upload
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Bulk transfer batch has been submitted successfully.
        </p>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={4} />

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#DCFCE7] text-4xl text-[#16A34A]">
          <FiCheck />
        </div>
        <h2 className="text-[22px] font-semibold text-[#1B163B]">
          Bulk Upload Submitted Successfully!
        </h2>
        <p className="mt-3 text-sm font-medium text-[#505E8E]">
          {createdCount || "-"} payout transfers have been created and are now pending approval.
        </p>

        <div className="mt-7 overflow-hidden rounded-[8px] border border-[#E0E5F2] text-left">
          <DetailRow
            label="Batch ID"
            value={
              <span className="flex items-center justify-between gap-4">
                {displayText(batchNo)}
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(displayText(batchNo))}
                  aria-label="Copy batch ID"
                >
                  <FiCopy className="shrink-0 text-[#505E8E]" />
                </button>
              </span>
            }
          />
          <DetailRow label="File Name" value={displayText(draft.fileName)} />
          <DetailRow label="Transfers Created" value={String(createdCount || "-")} />
          <DetailRow label="Total Amount" value={formatINR(totalAmount)} />
          <DetailRow
            label="Status"
            value={
              <span className="rounded-[5px] bg-[#FEF3C7] px-2 py-1 text-xs font-semibold text-[#F59E0B]">
                Pending Approval
              </span>
            }
          />
        </div>

        <div className="mt-6 rounded-[8px] border border-[#E0E5F2] bg-white p-5 text-left">
          <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
            What's Next?
          </h3>
          <p className="mb-3 text-sm font-medium text-[#505E8E]">
            {"\u2022"} Track this batch from the Transfers page.
          </p>
          <p className="text-sm font-medium text-[#505E8E]">
            {"\u2022"} Approvers will receive notifications for the submitted batch.
          </p>
        </div>
      </section>

      <div className="mt-8 flex max-w-[760px] flex-wrap items-center justify-between gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to="/payouts/transfers"
          className="flex h-10 items-center rounded-[6px] border border-[#1B163B] px-7 text-sm font-semibold text-[#1B163B]"
        >
          View Transfers
        </Link>
        <Link
          to="/payouts/transfers/bulk-upload"
          className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          <FiPlus />
          Upload Another File
        </Link>
      </div>
    </div>
  );
}

export default PayoutBulkUploadConfirm;
